// :cow: Cosmose CONFIDENTIAL :iso:
import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { AuthService } from './auth.service';
import { catchError, concatMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { State } from '@med/app/store';
import { authLogout, authRefreshToken } from '../store/auth.actions';
import { AuthRepository } from './auth.repository';
import { Observable, Subject, throwError } from 'rxjs';
import { AuthData } from '@med/app/auth/model';
import { ConfigService } from '@med/app/core/config/config.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private reauthorize$: Subject<AuthData>;

  constructor(
    private authService: AuthService,
    private authRepository: AuthRepository,
    private store: Store<State>,
    private configService: ConfigService,
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const excludedUrls = [
      `${ this.configService.getConfig().apiUrl }/uaa/oauth/token`,
      `${ this.configService.getConfig().apiUrl }/uaa/users/password/change`,
    ];
    const authorizedUrls = [
      this.configService.getConfig().apiUrl,
      this.configService.getConfig().lockscreenApi,
    ];

    const exists = authorizedUrls.some((url) => req.url.includes(url));
    const notAuthorizedUrl = (!exists) || excludedUrls.includes(req.url);
    if (notAuthorizedUrl) {
      return next.handle(req);
    }
    const accessToken = this.authService.getAccessToken();
    const authReq = this.setAuthorizationHeaders(req, accessToken);

    return next
      .handle(authReq)
      .pipe(
        catchError(error => {
          if (error.status === 401) {
            return this.handleUnauthorizedStatus(req, next);
          }
          return throwError(error || 'Server error');
        }),
      );
  }

  setAuthorizationHeaders(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      headers: token
        ? req.headers.set('Authorization', `Bearer ${ token }`)
        : req.headers.set('Authorization', `Basic ${ this.configService.getConfig().securityToken }`),
    });
  }

  handleUnauthorizedStatus(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.reauthorize$) {
      const refreshToken = this.authService.getRefreshToken();
      this.reauthorize$ = new Subject<AuthData>();
      this.authRepository.reauthorize(refreshToken)
        .subscribe(authData => {
            this.store.dispatch(authRefreshToken({authData}));
            this.reauthorize$.next(authData);
            this.reauthorize$.complete();
            this.reauthorize$ = undefined;
          },
          () => {
            this.reauthorize$.complete();
            this.reauthorize$ = undefined;
            this.store.dispatch(authLogout());
          });
    }

    return this.reauthorize$.pipe(
      take(1),
      concatMap(authData => {
        return next.handle(this.setAuthorizationHeaders(req, authData.access_token));
      }),
      catchError(error => throwError(error || 'Server error')),
    );
  }
}
